import React, { useState, useEffect } from 'react';
import {
  Book, Info, Image, GraduationCap, Settings, CheckCircle,
  ChevronLeft, ChevronRight, Check, X, ArrowLeft
} from 'lucide-react';
import './CreateCourseTab.css';

// Import tab components from EditCourse
import { BasicInfoTab } from './Components/EditCourse/BasicInfoTab';
import { CourseDetailsTab } from './Components/EditCourse/CourseDetailsTab';
import { MediaTab } from './Components/EditCourse/MediaTab';
import { CourseContentTab } from './Components/EditCourse/CourseContentTab';
import SectionsLessonsTab from './Components/EditCourse/SectionsLessonsTab';
import OfferingsSessionsTab from './Components/EditCourse/OfferingsSessionsTab';
import { SettingsTab } from './Components/EditCourse/SettingsTab';
import { ReviewTab } from './Components/EditCourse/ReviewTab';
import type { FAQ } from './Components/EditCourse/CourseDetailsTab';
import type { SelectedModule, Offering, Session } from './Components/EditCourse/CourseContentTab';

// Import services
import {
  updateCourse,
  getCourseDetails,
  getCategories,
  getSubCategories,
  getLanguages,
  addCategory,
  addSubCategory,
  type Category,
  type SubCategory,
  type Language,
} from '../../../services/AdminService/AdminCourseManagementService';
import {
  uploadCourseThumbnail,
} from '../../../services/UploadService';
import UserService, { type UserDetails } from '../../../services/UserService';
import { saveApprovalConfig, requestApproval, getApprovalConfig } from '../../../services/PagesServices/CourseApprovalService';

// Tab configuration
interface Tab {
  id: string;
  label: string;
  icon: typeof Book;
  step: number;
  description: string;
}

const tabs: Tab[] = [
  { id: 'basic', label: 'Basic Info', icon: Book, step: 1, description: 'Course fundamentals' },
  { id: 'details', label: 'Course Details', icon: Info, step: 2, description: 'Additional information' },
  { id: 'media', label: 'Media & Assets', icon: Image, step: 3, description: 'Visual content' },
  { id: 'sections', label: 'Sections & Lessons', icon: GraduationCap, step: 4, description: 'Course structure' },
  { id: 'offerings', label: 'Offerings & Sessions', icon: GraduationCap, step: 5, description: 'Training schedules (optional)' },
  { id: 'settings', label: 'Settings', icon: Settings, step: 6, description: 'Rules & enrollment' },
  { id: 'review', label: 'Review & Publish', icon: CheckCircle, step: 7, description: 'Final review' },
];

interface EditCourseTabProps {
  courseId: number;
  onBack: () => void;
}

const EditCourseTab: React.FC<EditCourseTabProps> = ({ courseId, onBack }) => {
  // Tab navigation state
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [completedTabs, setCompletedTabs] = useState<string[]>([]);

  // Current user state
  const [currentUser, setCurrentUser] = useState<UserDetails | null>(null);
  const [instructorName, setInstructorName] = useState('Loading...');

  // Metadata state
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loadingMetadata, setLoadingMetadata] = useState(true);

  // Basic Info state
  const [courseType, setCourseType] = useState<'web' | 'ilt' | 'blended'>('ilt');
  const [courseTitle, setCourseTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [language, setLanguage] = useState('');
  const [level, setLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [courseDuration, setCourseDuration] = useState('');

  // Course Details state
  const [tags, setTags] = useState<string[]>([]);
  const [requirements, setRequirements] = useState<string[]>(['']);
  const [outcomes, setOutcomes] = useState<string[]>(['']);
  const [faqs, setFAQs] = useState<FAQ[]>([{ question: '', answer: '' }]);

  // Media state
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [existingThumbnail, setExistingThumbnail] = useState<string | null>(null);

  // Course Content state - Modules
  const [selectedModules, setSelectedModules] = useState<SelectedModule[]>([]);

  // Course Content state - ILT Offerings
  const [offerings, setOfferings] = useState<Offering[]>([]);

  // Course Content state - ILT Sessions
  const [sessions, setSessions] = useState<Session[]>([]);

  // Settings state
  const [visibilityType, setVisibilityType] = useState<'public' | 'restricted'>('public');
  const [requiredModules, setRequiredModules] = useState(false);
  const [minimumScore, setMinimumScore] = useState('70');
  const [sequentialLearning, setSequentialLearning] = useState(false);
  const [managerApproval, setManagerApproval] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sendReminders, setSendReminders] = useState(false);


  // Approval workflow state (prefilled from saved config)
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [approverUserId, setApproverUserId] = useState('');
  const [approverEmail, setApproverEmail] = useState('');
  const [initialApprovalKey, setInitialApprovalKey] = useState('');

  // Loading and error states
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Add Category/Subcategory modal states
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSubcategory, setAddingSubcategory] = useState(false);

  // Load current user and metadata on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setLoadingMetadata(true);
      setError(null);
      try {
        // Load current user profile
        const userProfile = await UserService.getOwnProfile();
        if (!userProfile || !userProfile.uuid) {
          throw new Error('Failed to load user profile. Please ensure you are logged in.');
        }
        setCurrentUser(userProfile);

        // Set instructor name from current user
        const fullName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Current User';
        setInstructorName(fullName);

        // Load metadata
        const [categoriesRes, subcategoriesRes, languagesRes] = await Promise.all([
          getCategories(),
          getSubCategories(),
          getLanguages(),
        ]);

        if (categoriesRes.success && categoriesRes.data) {
          setCategories(categoriesRes.data);
        }

        if (subcategoriesRes.success && subcategoriesRes.data) {
          setSubcategories(subcategoriesRes.data);
        }

        if (languagesRes.success && languagesRes.data) {
          setLanguages(languagesRes.data);
        }

        // Check if critical metadata failed to load
        if (!categoriesRes.success || !languagesRes.success) {
          setError('Failed to load course metadata. Some options may not be available.');
        }
      } catch (err: any) {
        console.error('Failed to load initial data:', err);
        const errorMessage = err.message || 'Failed to load course data. Please refresh the page.';
        setError(errorMessage);
        setInstructorName('Unable to load user');
      } finally {
        setLoadingMetadata(false);
      }
    };

    loadInitialData();
  }, []);

  // Load course data for editing
  useEffect(() => {
    const loadCourseData = async () => {
      // Wait for metadata to load first
      if (loadingMetadata) {
        console.log('Waiting for metadata to load before fetching course data...');
        return;
      }

      setIsUpdating(true);
      setError(null);

      try {
        console.log('=== Loading course data for ID:', courseId, '===');
        const result = await getCourseDetails(courseId);

        if (!result.success || !result.data) {
          throw new Error(result.error?.message || 'Failed to load course details');
        }

        const courseData = result.data.course || result.data;
        const apiData = result.data;
        console.log('Raw course data received:', courseData);
        console.log('Full API response:', apiData);

        // Populate basic info
        setCourseTitle(courseData.title || '');
        setShortDescription(courseData.shortDescription || '');
        setDescription(courseData.description || '');

        // Set category, subcategory, language with detailed logging
        const categoryId = courseData.categoryId;
        const subcategoryId = courseData.subCategoryId;
        const languageId = courseData.languageId;

        const categoryIdStr = categoryId?.toString() || '';
        const subcategoryIdStr = subcategoryId?.toString() || '';
        const languageIdStr = languageId?.toString() || '';

        console.log('Setting form values:');
        console.log('  Category ID:', categoryIdStr, '(from:', categoryId, ')');
        console.log('  Subcategory ID:', subcategoryIdStr, '(from:', subcategoryId, ')');
        console.log('  Language ID:', languageIdStr, '(from:', languageId, ')');
        console.log('  Level:', courseData.level);
        console.log('  Duration:', courseData.courseDuration);

        setCategory(categoryIdStr);
        setSubcategory(subcategoryIdStr);
        setLanguage(languageIdStr);
        setLevel(courseData.level as 'beginner' | 'intermediate' | 'advanced' || 'beginner');
        setCourseDuration(courseData.courseDuration || '');

        // Verify against available metadata
        console.log('Available metadata:');
        console.log('  Categories count:', categories.length);
        console.log('  Subcategories count:', subcategories.length);
        console.log('  Languages count:', languages.length);

        const selectedCategory = categories.find(c => c.id === categoryId);
        const selectedSubcategory = subcategories.find(s => s.id === subcategoryId);
        const selectedLanguage = languages.find(l => l.id === languageId);

        console.log('  Selected category:', selectedCategory?.name || 'NOT FOUND');
        console.log('  Selected subcategory:', selectedSubcategory?.name || 'NOT FOUND');
        console.log('  Selected language:', selectedLanguage?.name || 'NOT FOUND');

        // Populate course details
        const metaKeywords = courseData.metaKeywords;
        if (metaKeywords) {
          const keywordsArray = typeof metaKeywords === 'string'
            ? metaKeywords.split(',').map(k => k.trim()).filter(k => k)
            : [];
          setTags(keywordsArray);
          console.log('  Tags:', keywordsArray.length, ':', keywordsArray);
        }

        // Populate requirements - API uses "requirement" field
        if (apiData.requirements && apiData.requirements.length > 0) {
          const requirementsArray = apiData.requirements.map((r: any) =>
            r.requirement || r.content || r.requirementText || ''
          ).filter((r: string) => r.trim());
          setRequirements(requirementsArray.length > 0 ? requirementsArray : ['']);
          console.log('  Requirements:', requirementsArray.length, ':', requirementsArray);
        }

        // Populate outcomes - API uses "outcome" field
        if (apiData.outcomes && apiData.outcomes.length > 0) {
          const outcomesArray = apiData.outcomes.map((o: any) =>
            o.outcome || o.content || o.outcomeText || ''
          ).filter((o: string) => o.trim());
          setOutcomes(outcomesArray.length > 0 ? outcomesArray : ['']);
          console.log('  Outcomes:', outcomesArray.length, ':', outcomesArray);
        }

        // Populate FAQs
        if (apiData.faqs && apiData.faqs.length > 0) {
          const faqsArray = apiData.faqs.map((f: any) => ({
            question: f.question || '',
            answer: f.answer || ''
          })).filter((f: any) => f.question.trim() && f.answer.trim());
          setFAQs(faqsArray.length > 0 ? faqsArray : [{ question: '', answer: '' }]);
          console.log('  FAQs:', faqsArray.length, ':', faqsArray);
        }

        // Set instructor name
        const instructorNameFromApi = courseData.instructorName || instructorName;
        setInstructorName(instructorNameFromApi);
        console.log('  Instructor:', instructorNameFromApi);

        // Set existing thumbnail
        if (courseData.thumbnail) {
          setExistingThumbnail(courseData.thumbnail);
          console.log('  Thumbnail:', courseData.thumbnail);
        }


        // Load the approval configuration (prefill the Approval section)
        try {
          const apprRes = await getApprovalConfig(courseId);
          if (apprRes.success && apprRes.data) {
            const a = apprRes.data;
            const ra = !!a.requiresApproval;
            const uid = a.approverType === 'user' ? (a.approverUuid || '') : '';
            const email = a.approverType === 'email' ? (a.approverEmail || '') : '';
            setRequiresApproval(ra);
            setApproverUserId(uid);
            setApproverEmail(email);
            setInitialApprovalKey(`${ra}|${uid}|${email}`);
          }
        } catch (e) {
          console.warn('Could not load approval config:', e);
        }

        console.log('=== Course data loaded successfully ===');
        setSuccess(`Loaded course: ${courseData.title}`);
        setTimeout(() => setSuccess(null), 3000);

      } catch (err: any) {
        console.error('Error loading course for edit:', err);
        setError(err.message || 'Failed to load course');
      } finally {
        setIsUpdating(false);
      }
    };

    loadCourseData();
  }, [courseId, loadingMetadata, categories, subcategories, languages]);

  // Helper functions for course details
  const handleAddTag = (tag: string) => {
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const handleAddRequirement = () => {
    setRequirements([...requirements, '']);
  };

  const handleRemoveRequirement = (index: number) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const handleUpdateRequirement = (index: number, value: string) => {
    setRequirements(requirements.map((item, i) => (i === index ? value : item)));
  };

  const handleAddOutcome = () => {
    setOutcomes([...outcomes, '']);
  };

  const handleRemoveOutcome = (index: number) => {
    setOutcomes(outcomes.filter((_, i) => i !== index));
  };

  const handleUpdateOutcome = (index: number, value: string) => {
    setOutcomes(outcomes.map((item, i) => (i === index ? value : item)));
  };

  const handleAddFAQ = () => {
    setFAQs([...faqs, { question: '', answer: '' }]);
  };

  const handleRemoveFAQ = (index: number) => {
    setFAQs(faqs.filter((_, i) => i !== index));
  };

  const handleUpdateFAQ = (index: number, faq: FAQ) => {
    setFAQs(faqs.map((item, i) => (i === index ? faq : item)));
  };

  // Add Category handler
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Category name is required');
      return;
    }

    setAddingCategory(true);
    setError(null);

    try {
      const result = await addCategory(newCategoryName.trim());

      if (result.success && result.data) {
        // Add the new category to the list
        setCategories([...categories, result.data]);

        // Select the newly added category
        setCategory(result.data.id.toString());

        // Reset subcategory since we changed categories
        setSubcategory('');

        // Close modal and reset form
        setShowAddCategoryModal(false);
        setNewCategoryName('');
        setSuccess('Category added successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(result.error?.message || 'Failed to add category');
      }
    } catch (err: any) {
      console.error('Error adding category:', err);
      setError(err.message || 'Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  // Add Subcategory handler
  const handleAddSubcategory = async () => {
    if (!category) {
      setError('Please select a category first');
      return;
    }

    if (!newSubcategoryName.trim()) {
      setError('Subcategory name is required');
      return;
    }

    setAddingSubcategory(true);
    setError(null);

    try {
      const result = await addSubCategory(parseInt(category), newSubcategoryName.trim());

      if (result.success && result.data) {
        // Add the new subcategory to the list
        setSubcategories([...subcategories, result.data]);

        // Select the newly added subcategory
        setSubcategory(result.data.id.toString());

        // Close modal and reset form
        setShowAddSubcategoryModal(false);
        setNewSubcategoryName('');
        setSuccess('Subcategory added successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error(result.error?.message || 'Failed to add subcategory');
      }
    } catch (err: any) {
      console.error('Error adding subcategory:', err);
      setError(err.message || 'Failed to add subcategory');
    } finally {
      setAddingSubcategory(false);
    }
  };

  // Validation
  const validateCurrentTab = () => {
    const errors: Record<string, string> = {};

    switch (activeTab) {
      case 'basic':
        if (!courseTitle.trim()) errors.courseTitle = 'Course title is required';
        if (!category) errors.category = 'Category is required';
        if (!subcategory) errors.subcategory = 'Subcategory is required';
        if (!language) errors.language = 'Language is required';
        break;
      case 'details':
        // Optional: Add validation if needed
        break;
      case 'media':
        // Optional: Thumbnail can be skipped
        break;
      case 'settings':
        // All optional
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigation
  const handleNext = async () => {
    if (!validateCurrentTab()) {
      return;
    }

    // Mark current tab as completed
    if (!completedTabs.includes(activeTab)) {
      setCompletedTabs([...completedTabs, activeTab]);
    }

    // Move to next tab
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
      setCurrentStep(currentIndex + 2);
    }
  };

  const handlePrevious = () => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1].id);
      setCurrentStep(currentIndex);
    }
  };

  // Update course handler
  const handleUpdateCourse = async () => {
    // Validation
    if (!courseTitle.trim()) {
      setError('Course title is required');
      return;
    }
    if (!category) {
      setError('Category is required');
      return;
    }
    if (!subcategory) {
      setError('Subcategory is required');
      return;
    }
    if (!language) {
      setError('Language is required');
      return;
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // Ensure we have current user
      if (!currentUser) {
        throw new Error('User information not loaded. Please refresh the page.');
      }

      // Prepare course payload with all details
      const coursePayload = {
        title: courseTitle,
        instructor: instructorName,
        shortDescription: shortDescription || undefined,
        description: description || undefined,
        languageId: parseInt(language),
        categoryId: parseInt(category),
        subCategoryId: parseInt(subcategory),
        level: level,
        courseDuration: courseDuration || undefined,
        metaKeywords: tags,
        outcomes: outcomes.filter(o => o.trim()),
        requirements: requirements.filter(r => r.trim()),
        faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        lessons: [],
        // batchIds / availableToAllBatches are deliberately omitted: the update
        // endpoint only reassigns batches when one of them is present, so leaving
        // them out preserves whatever the course already has.
      };

      console.log('Updating course with ID:', courseId);
      console.log('Update payload:', coursePayload);

      const updateResult = await updateCourse(courseId, coursePayload);

      if (!updateResult.success) {
        throw new Error(updateResult.error?.message || 'Failed to update course');
      }

      console.log('Course updated successfully');

      // Upload thumbnail if provided
      if (thumbnailFile) {
        console.log('Uploading thumbnail...');
        const uploadResult = await uploadCourseThumbnail(thumbnailFile, courseId);
        if (!uploadResult.success) {
          console.error('Thumbnail upload failed:', uploadResult.error);
        }
      }

      // Persist the approval configuration; re-send the request/notice only if it changed.
      const approvalKey = `${requiresApproval}|${approverUserId}|${approverEmail}`;
      const approvalChanged = approvalKey !== initialApprovalKey;
      await saveApprovalConfig(courseId, {
        requiresApproval,
        approverUuid: requiresApproval && approverUserId ? approverUserId : undefined,
        approverEmail: requiresApproval && !approverUserId ? (approverEmail || undefined) : undefined,
      });
      if (approvalChanged && requiresApproval && (approverUserId || approverEmail)) {
        await requestApproval(courseId);
      }
      setInitialApprovalKey(approvalKey);

      setSuccess('Course updated successfully!');

      // Go back to list after a short delay
      setTimeout(() => {
        onBack();
      }, 2000);

    } catch (err: any) {
      console.error('Error updating course:', err);
      setError(err.message || 'Failed to update course');
    } finally {
      setIsUpdating(false);
    }
  };

  // Get display names for review tab
  const getCategoryName = () => categories.find(c => c.id === parseInt(category))?.name || '';
  const getSubcategoryName = () => subcategories.find(s => s.id === parseInt(subcategory))?.name || '';
  const getLanguageName = () => languages.find(l => l.id === parseInt(language))?.name || '';

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            courseType={courseType}
            courseTitle={courseTitle}
            instructorName={instructorName}
            shortDescription={shortDescription}
            description={description}
            category={category}
            subcategory={subcategory}
            language={language}
            level={level}
            courseDuration={courseDuration}
            categories={categories}
            subcategories={subcategories}
            languages={languages}
            loadingMetadata={loadingMetadata}
            onCourseTypeChange={setCourseType}
            onCourseTitleChange={setCourseTitle}
            onShortDescriptionChange={setShortDescription}
            onDescriptionChange={setDescription}
            onCategoryChange={setCategory}
            onSubcategoryChange={setSubcategory}
            onLanguageChange={setLanguage}
            onLevelChange={setLevel}
            onCourseDurationChange={setCourseDuration}
            validationErrors={validationErrors}
            onAddCategoryClick={() => setShowAddCategoryModal(true)}
            onAddSubcategoryClick={() => setShowAddSubcategoryModal(true)}
          />
        );

      case 'details':
        return (
          <CourseDetailsTab
            tags={tags}
            requirements={requirements}
            outcomes={outcomes}
            faqs={faqs}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onAddRequirement={handleAddRequirement}
            onRemoveRequirement={handleRemoveRequirement}
            onUpdateRequirement={handleUpdateRequirement}
            onAddOutcome={handleAddOutcome}
            onRemoveOutcome={handleRemoveOutcome}
            onUpdateOutcome={handleUpdateOutcome}
            onAddFAQ={handleAddFAQ}
            onRemoveFAQ={handleRemoveFAQ}
            onUpdateFAQ={handleUpdateFAQ}
            validationErrors={validationErrors}
          />
        );

      case 'media':
        return (
          <MediaTab
            thumbnailFile={thumbnailFile}
            existingThumbnail={existingThumbnail}
            onThumbnailUpload={setThumbnailFile}
            onThumbnailRemove={() => {
              setThumbnailFile(null);
              setExistingThumbnail(null);
            }}
            validationErrors={validationErrors}
          />
        );

      case 'sections':
        return (
          <SectionsLessonsTab
            courseId={courseId}
            onUpdate={(sections) => {
              console.log('Sections updated:', sections);
            }}
          />
        );

      case 'offerings':
        return <OfferingsSessionsTab courseId={courseId} />;

      case 'settings':
        return (
          <SettingsTab
            visibilityType={visibilityType}
            onVisibilityTypeChange={setVisibilityType}
            requiredModules={requiredModules}
            minimumScore={minimumScore}
            sequentialLearning={sequentialLearning}
            managerApproval={managerApproval}
            onRequiredModulesChange={setRequiredModules}
            onMinimumScoreChange={setMinimumScore}
            onSequentialLearningChange={setSequentialLearning}
            onManagerApprovalChange={setManagerApproval}
            enrollmentType={enrollmentType}
            dueDate={dueDate}
            sendReminders={sendReminders}
            onEnrollmentTypeChange={setEnrollmentType}
            onDueDateChange={setDueDate}
            onSendRemindersChange={setSendReminders}
            requiresApproval={requiresApproval}
            approverUserId={approverUserId}
            approverEmail={approverEmail}
            onRequiresApprovalChange={setRequiresApproval}
            onApproverUserIdChange={setApproverUserId}
            onApproverEmailChange={setApproverEmail}
            validationErrors={validationErrors}
          />
        );

      case 'review':
        return (
          <ReviewTab
            courseType={courseType}
            courseTitle={courseTitle}
            instructorName={instructorName}
            shortDescription={shortDescription}
            description={description}
            categoryName={getCategoryName()}
            subcategoryName={getSubcategoryName()}
            languageName={getLanguageName()}
            level={level}
            courseDuration={courseDuration}
            tags={tags}
            requirements={requirements}
            outcomes={outcomes}
            faqCount={faqs.filter(f => f.question.trim() && f.answer.trim()).length}
            thumbnailFile={thumbnailFile}
            existingThumbnail={existingThumbnail}
            modulesCount={selectedModules.length}
            offeringsCount={offerings.length}
            sessionsCount={sessions.length}
            visibilityType={visibilityType}
            isCreating={isUpdating}
            onCreateCourse={handleUpdateCourse}
            isUpdate={true}
            validationErrors={validationErrors}
            error={error}
            success={success}
          />
        );

      default:
        return null;
    }
  };

  // Calculate progress
  const progress = (currentStep / tabs.length) * 100;

  return (
    <div className="course-creator">
      {/* Back Button - Prominent at the top */}
      <div style={{
        marginBottom: '20px',
        padding: '12px 0',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#e5e7eb';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#f3f4f6';
          }}
        >
          <ArrowLeft size={18} />
          Back to Course List
        </button>
      </div>

      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="header-info">
            <h1>
              Edit Course
              <span style={{
                marginLeft: '15px',
                fontSize: '14px',
                fontWeight: 'normal',
                color: '#FF8C00',
                backgroundColor: '#FFF3E0',
                padding: '4px 12px',
                borderRadius: '4px'
              }}>
                ID: {courseId}
              </span>
            </h1>
            <p>Update your course content and settings</p>
          </div>
          <div className="progress-info">
            <div className="step-indicator">
              Step {currentStep} of {tabs.length}
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-tabs">
        <div className="nav-content">
          <div className="tabs-container">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isCompleted = completedTabs.includes(tab.id);

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentStep(tab.step);
                  }}
                  className={`tab-button ${isActive ? 'active' : ''} ${
                    isCompleted ? 'completed' : ''
                  }`}
                >
                  {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                  <div className="tab-info">
                    <div className="tab-label">{tab.label}</div>
                    <div className="tab-description">{tab.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Global Success/Error Messages */}
        {success && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '12px 20px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #c3e6cb'
          }}>
            {success}
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '12px 20px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#721c24',
                cursor: 'pointer',
                fontSize: '20px',
                padding: '0 5px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div className="content-container">{renderTabContent()}</div>

        {/* Navigation Buttons */}
        {activeTab !== 'review' && (
          <div className="nav-buttons">
            <button
              className="btn-secondary"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              <ChevronLeft size={20} />
              Previous
            </button>

            <button className="btn-primary" onClick={handleNext}>
              Next
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="trainer-coursemanagement-modal-overlay" onClick={() => setShowAddCategoryModal(false)}>
          <div className="trainer-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-coursemanagement-modal-header">
              <h2>Add New Category</h2>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => setShowAddCategoryModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-coursemanagement-modal-body">
              <div className="trainer-coursemanagement-modal-section">
                <label htmlFor="newCategoryName" style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Category Name
                </label>
                <input
                  id="newCategoryName"
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Programming, Business, Design"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingCategory) {
                      handleAddCategory();
                    }
                  }}
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-modal-footer">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={() => {
                  setShowAddCategoryModal(false);
                  setNewCategoryName('');
                }}
                disabled={addingCategory}
              >
                Cancel
              </button>
              <button
                className="trainer-coursemanagement-btn-primary"
                onClick={handleAddCategory}
                disabled={addingCategory || !newCategoryName.trim()}
              >
                {addingCategory ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subcategory Modal */}
      {showAddSubcategoryModal && (
        <div className="trainer-coursemanagement-modal-overlay" onClick={() => setShowAddSubcategoryModal(false)}>
          <div className="trainer-coursemanagement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-coursemanagement-modal-header">
              <h2>Add New Subcategory</h2>
              <button
                className="trainer-coursemanagement-icon-btn"
                onClick={() => setShowAddSubcategoryModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-coursemanagement-modal-body">
              {!category && (
                <div style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  border: '1px solid #ffeeba'
                }}>
                  Please select a category first before adding a subcategory.
                </div>
              )}

              <div className="trainer-coursemanagement-modal-section">
                <label htmlFor="categoryForSubcategory" style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Parent Category
                </label>
                <select
                  id="categoryForSubcategory"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>

                <label htmlFor="newSubcategoryName" style={{ fontWeight: 500, marginBottom: '8px', display: 'block' }}>
                  Subcategory Name
                </label>
                <input
                  id="newSubcategoryName"
                  type="text"
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                  placeholder="e.g., Web Development, Data Science"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  disabled={!category}
                  autoFocus={!!category}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !addingSubcategory && category) {
                      handleAddSubcategory();
                    }
                  }}
                />
              </div>
            </div>

            <div className="trainer-coursemanagement-modal-footer">
              <button
                className="trainer-coursemanagement-btn-secondary"
                onClick={() => {
                  setShowAddSubcategoryModal(false);
                  setNewSubcategoryName('');
                }}
                disabled={addingSubcategory}
              >
                Cancel
              </button>
              <button
                className="trainer-coursemanagement-btn-primary"
                onClick={handleAddSubcategory}
                disabled={addingSubcategory || !category || !newSubcategoryName.trim()}
              >
                {addingSubcategory ? 'Adding...' : 'Add Subcategory'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCourseTab;
